import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Ban, CheckCircle2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "@/components/ConfirmDialog";
import { WRITE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, WEEKDAY_NAMES } from "@/lib/api";

const STATUS_LABELS = { disponivel: "Disponível", bloqueado: "Bloqueado", indisponivel: "Indisponível" };
const BREAK_DEFS = [
  ["break_morning", "morning", "Café da manhã"],
  ["break_lunch", "lunch", "Almoço"],
  ["break_afternoon", "afternoon", "Café da tarde"],
];
const BREAK_LABELS = { morning: "Café manhã", lunch: "Almoço", afternoon: "Café tarde" };
const EMPTY_BREAKS = {
  break_morning: { enabled: false, start: "", end: "" },
  break_lunch: { enabled: false, start: "", end: "" },
  break_afternoon: { enabled: false, start: "", end: "" },
};
const EMPTY = { weekday: "0", start_time: "", end_time: "", status: "disponivel", note: "", ...EMPTY_BREAKS };

const breaksFromDoc = (avail) => {
  const out = { break_morning: { ...EMPTY_BREAKS.break_morning }, break_lunch: { ...EMPTY_BREAKS.break_lunch }, break_afternoon: { ...EMPTY_BREAKS.break_afternoon } };
  (avail.breaks || []).forEach((b) => {
    const def = BREAK_DEFS.find((d) => d[1] === b.kind);
    if (def) out[def[0]] = { enabled: !!b.enabled, start: b.start || "", end: b.end || "" };
  });
  return out;
};

const breaksToDoc = (form) =>
  BREAK_DEFS.map(([field, kind]) => ({
    kind,
    enabled: form[field].enabled,
    start: form[field].start || null,
    end: form[field].end || null,
  }));

export default function Availability() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [teachers, setTeachers] = useState([]);
  const [selected, setSelected] = useState(searchParams.get("teacher") || "");
  const [availabilities, setAvailabilities] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    api.get("/teachers").then((r) => {
      setTeachers(r.data.filter((t) => t.active));
      if (!searchParams.get("teacher") && r.data.length > 0) {
        setSelected(r.data[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (teacherId) => {
    if (!teacherId) return;
    const { data } = await api.get("/availabilities", { params: { teacher_id: teacherId } });
    setAvailabilities(data);
  };

  useEffect(() => {
    load(selected);
    if (selected) setSearchParams({ teacher: selected });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const byWeekday = useMemo(() => {
    const map = {};
    for (let d = 0; d < 7; d++) map[d] = [];
    availabilities.forEach((a) => map[a.weekday]?.push(a));
    return map;
  }, [availabilities]);

  const teacher = teachers.find((t) => t.id === selected);

  const openDialog = (weekday, avail) => {
    setEditing(avail || null);
    setForm(
      avail
        ? { weekday: String(avail.weekday), start_time: avail.start_time, end_time: avail.end_time, status: avail.status, note: avail.note || "", ...breaksFromDoc(avail) }
        : { ...EMPTY, weekday: String(weekday) }
    );
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        teacher_id: selected,
        weekday: Number(form.weekday),
        start_time: form.start_time,
        end_time: form.end_time,
        status: form.status,
        note: form.note || null,
        breaks: breaksToDoc(form),
      };
      if (editing) await api.put(`/availabilities/${editing.id}`, payload);
      else await api.post("/availabilities", payload);
      toast.success("Disponibilidade salva.");
      setDialogOpen(false);
      load(selected);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleBlock = async (a) => {
    const next = a.status === "disponivel" ? "bloqueado" : "disponivel";
    try {
      await api.put(`/availabilities/${a.id}`, {
        teacher_id: a.teacher_id, weekday: a.weekday, start_time: a.start_time,
        end_time: a.end_time, status: next, note: a.note, breaks: a.breaks || [],
      });
      toast.success(next === "bloqueado" ? "Horário bloqueado." : "Horário liberado.");
      load(selected);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/availabilities/${deleting.id}`);
      toast.success("Horário removido.");
      setDeleting(null);
      load(selected);
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  const setBreak = (field, patch) => setForm((f) => ({ ...f, [field]: { ...f[field], ...patch } }));

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Disponibilidade</h1>
          <p className="mt-1 text-sm text-muted-foreground">Janelas semanais de horário de cada professor.</p>
        </div>
        <div className="w-full sm:w-72">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Professor</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="availability-teacher-select">
              <SelectValue placeholder="Selecione o professor" />
            </SelectTrigger>
            <SelectContent>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`availability-teacher-option-${t.name}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {teacher && (
        <div className="flex flex-wrap gap-1" data-testid="availability-teacher-courses">
          {(teacher.course_names || []).map((n) => (
            <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {WEEKDAY_NAMES.map((name, day) => (
          <div key={day} className="border border-border bg-white" data-testid={`availability-day-${day}`}>
            <div className="flex items-center justify-between border-b border-border bg-zinc-50 px-3 py-2">
              <span className="font-heading text-sm font-semibold">{name}</span>
              {canWrite && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(day, null)} data-testid={`availability-add-${day}`}>
                  <Plus size={14} />
                </Button>
              )}
            </div>
            <div className="space-y-2 p-2">
              {byWeekday[day].length === 0 && (
                <p className="p-1 text-xs text-muted-foreground">Sem horários</p>
              )}
              {byWeekday[day].map((a) => (
                <div key={a.id} className="border border-border p-2" data-testid={`availability-slot-${a.id}`}>
                  <p className="font-heading text-sm font-bold">
                    {a.start_time}–{a.end_time}
                  </p>
                  <Badge
                    className={`mt-1 text-[10px] ${a.status === "disponivel" ? "bg-primary text-primary-foreground" : a.status === "bloqueado" ? "bg-destructive text-destructive-foreground" : ""}`}
                    variant={a.status === "indisponivel" ? "secondary" : "default"}
                  >
                    {STATUS_LABELS[a.status]}
                  </Badge>
                  {(a.breaks || []).filter((b) => b.enabled).length > 0 && (
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`availability-breaks-${a.id}`}>
                      Intervalos: {(a.breaks || []).filter((b) => b.enabled).map((b) => `${BREAK_LABELS[b.kind]} ${b.start}–${b.end}`).join(" · ")}
                    </p>
                  )}
                  {a.note && <p className="mt-1 text-[11px] text-muted-foreground">{a.note}</p>}
                  {canWrite && (
                    <div className="mt-2 flex gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" title={a.status === "disponivel" ? "Bloquear" : "Liberar"} onClick={() => toggleBlock(a)} data-testid={`availability-toggle-${a.id}`}>
                        {a.status === "disponivel" ? <Ban size={12} /> : <CheckCircle2 size={12} className="text-primary" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openDialog(day, a)} data-testid={`availability-edit-${a.id}`}>
                        <Pencil size={12} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setDeleting(a)} data-testid={`availability-delete-${a.id}`}>
                        <Trash2 size={12} className="text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="availability-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar horário" : "Novo horário"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="availability-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Dia da semana</Label>
              <Select value={form.weekday} onValueChange={(v) => setForm({ ...form, weekday: v })}>
                <SelectTrigger data-testid="availability-weekday-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_NAMES.map((n, i) => (
                    <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Início</Label>
                <Input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="availability-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Fim</Label>
                <Input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="availability-end-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="availability-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponivel">Disponível</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  <SelectItem value="indisponivel">Indisponível</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Intervalos (não disponíveis para aulas)</Label>
              <div className="space-y-2 border border-border p-3">
                {BREAK_DEFS.map(([field, , label]) => (
                  <div key={field} className="flex items-center gap-2" data-testid={`availability-${field}-row`}>
                    <input
                      type="checkbox"
                      checked={form[field].enabled}
                      onChange={(e) => setBreak(field, { enabled: e.target.checked })}
                      className="h-4 w-4 accent-primary"
                      data-testid={`availability-${field}-enabled`}
                    />
                    <span className="w-28 shrink-0 text-sm">{label}</span>
                    <Input
                      type="time"
                      disabled={!form[field].enabled}
                      value={form[field].start}
                      onChange={(e) => setBreak(field, { start: e.target.value })}
                      className="h-8"
                      data-testid={`availability-${field}-start`}
                    />
                    <span className="text-xs text-muted-foreground">até</span>
                    <Input
                      type="time"
                      disabled={!form[field].enabled}
                      value={form[field].end}
                      onChange={(e) => setBreak(field, { end: e.target.value })}
                      className="h-8"
                      data-testid={`availability-${field}-end`}
                    />
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">Se nenhum intervalo estiver habilitado, o comportamento atual é mantido.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observação</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="availability-note-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="availability-save-button">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Remover horário"
        description={`Remover o horário ${deleting?.start_time}–${deleting?.end_time}?`}
        onConfirm={remove}
        testId="availability-delete-confirm"
      />
    </div>
  );
}
